import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type Cliente } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

type Props = {
  value: number | null;
  onChange: (cliente: Cliente | null) => void;
  /** opcional: cuando se crea un cliente, refrescar lista global */
  onClienteCreated?: (c: Cliente) => void;
  /** Enfocar el input al montar (util cuando vive dentro de un modal recien abierto). */
  autoFocus?: boolean;
  /** Pre-llenar con un nombre (p. ej. de la IA): matchea uno existente o deja el texto para crearlo. */
  nombrePrefill?: string;
};

/**
 * Combobox de clientes con busqueda en vivo + creacion inline.
 *
 * - Click en el input abre dropdown con todos los clientes (ordenados por
 *   uso descendente).
 * - Al escribir filtra en vivo (substring case-insensitive).
 * - Si no hay match exacto y hay texto, ofrece "+ Crear: <texto>".
 * - Click outside o ESC cierra el dropdown.
 * - X en el input limpia la seleccion.
 *
 * Sirve para listas grandes (150+ clientes). No carga via fetch del query;
 * trae todos los clientes una vez (la API es /api/clientes que es liviana
 * por cliente, ~50 bytes por item) y filtra cliente-side.
 */
export default function ClientePicker({
  value,
  onChange,
  onClienteCreated,
  autoFocus,
  nombrePrefill,
}: Props) {
  const toast = useToast();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [crearOpen, setCrearOpen] = useState(false);
  const [crearNombre, setCrearNombre] = useState("");
  const [creando, setCreando] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function loadClientes() {
    try {
      const list = await api.get<Cliente[]>("/api/clientes");
      setClientes(list);
    } catch {
      // noop, error de red
    }
  }

  useEffect(() => {
    void loadClientes();
    if (autoFocus) {
      // Ligero defer para dejar que Radix termine de animar el modal padre
      // antes de robar foco; evita perderlo si Radix lo reasigna al portal.
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cerrar al click outside
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Pre-llenar desde la IA: matchea un cliente existente por nombre, o deja el
  // texto escrito para que el usuario lo cree con "+ Crear cliente nuevo".
  const prefillRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!nombrePrefill || value !== null || clientes.length === 0) return;
    if (prefillRef.current === nombrePrefill) return;
    prefillRef.current = nombrePrefill;
    const match = clientes.find(
      (c) => c.nombre.toLowerCase() === nombrePrefill.trim().toLowerCase(),
    );
    if (match) {
      onChange(match);
      setQuery(match.nombre);
    } else {
      setQuery(nombrePrefill.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, nombrePrefill, value]);

  const seleccionado = useMemo(
    () => clientes.find((c) => c.id === value) ?? null,
    [clientes, value],
  );

  const filtrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clientes.slice(0, 80);
    return clientes
      .filter((c) => c.nombre.toLowerCase().includes(q))
      .slice(0, 80);
  }, [clientes, query]);

  const matchExacto = useMemo(
    () =>
      query.trim() &&
      clientes.some((c) => c.nombre.toLowerCase() === query.trim().toLowerCase()),
    [clientes, query],
  );

  function selectCliente(c: Cliente) {
    onChange(c);
    setQuery(c.nombre);
    setOpen(false);
  }

  function clearSeleccion() {
    onChange(null);
    setQuery("");
    inputRef.current?.focus();
    setOpen(true);
  }

  function abrirModalCrear() {
    setCrearNombre(query.trim());
    setCrearOpen(true);
    setOpen(false);
  }

  async function crearCliente(e: FormEvent) {
    e.preventDefault();
    const nombre = crearNombre.trim();
    if (!nombre) return;
    setCreando(true);
    try {
      const c = await api.post<Cliente>("/api/clientes", { nombre });
      toast.success("Cliente creado", c.nombre);
      setClientes((prev) => [c, ...prev]);
      onChange(c);
      setQuery(c.nombre);
      setCrearOpen(false);
      onClienteCreated?.(c);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  // Sync display al cambiar value externo
  useEffect(() => {
    if (seleccionado && query !== seleccionado.nombre) {
      setQuery(seleccionado.nombre);
    }
    if (!seleccionado && query && clientes.find((c) => c.id === value) === undefined) {
      // value=null y aun hay texto -> dejar el texto para creacion
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, seleccionado]);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex gap-1">
        <input
          ref={inputRef}
          type="text"
          className="input flex-1"
          placeholder="Busca o escribe un cliente nuevo..."
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (seleccionado && e.target.value !== seleccionado.nombre) {
              onChange(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Enter" && open) {
              e.preventDefault();
              if (filtrados.length > 0) {
                selectCliente(filtrados[0]);
              } else if (query.trim() && !matchExacto) {
                abrirModalCrear();
              }
            } else if (e.key === "ArrowDown") {
              setOpen(true);
            }
          }}
        />
        {seleccionado && (
          <button
            type="button"
            onClick={clearSeleccion}
            aria-label={`Quitar el cliente ${seleccionado.nombre}`}
            className="btn-ghost text-xs px-2"
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-ink-700 bg-ink-900 shadow-2xl max-h-80 overflow-y-auto">
          {filtrados.length === 0 && !query.trim() ? (
            <p className="px-3 py-2 text-sm text-zinc-500">No hay clientes registrados.</p>
          ) : filtrados.length === 0 ? (
            <p className="px-3 py-2 text-sm text-zinc-500">Sin coincidencias para "{query}".</p>
          ) : (
            <ul className="divide-y divide-ink-800">
              {filtrados.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => selectCliente(c)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-ink-800 ${
                      seleccionado?.id === c.id ? "bg-ink-800/70 text-accent-400" : "text-zinc-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="min-w-0 truncate">{c.nombre}</span>
                      <span className="shrink-0 whitespace-nowrap pl-2 text-xs text-zinc-500">{c.usos} casos</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {query.trim() && !matchExacto && (
            <button
              type="button"
              onClick={abrirModalCrear}
              className="w-full text-left px-3 py-2 text-sm text-accent-300 border-t border-ink-700 hover:bg-ink-800"
            >
              + Crear cliente nuevo: "{query.trim()}"
            </button>
          )}
        </div>
      )}

      <Modal
        open={crearOpen}
        onOpenChange={(v) => !v && setCrearOpen(false)}
        title="Nuevo cliente"
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setCrearOpen(false)} className="btn-ghost">Cancelar</button>
            <button
              type="button"
              onClick={crearCliente}
              disabled={!crearNombre.trim() || creando}
              className="btn-primary disabled:opacity-50"
            >
              {creando ? "Creando..." : "Crear"}
            </button>
          </>
        }
      >
        <form onSubmit={crearCliente}>
          <span className="text-xs uppercase tracking-wide text-zinc-400">Nombre del cliente</span>
          <input
            type="text"
            autoFocus
            className="input mt-1"
            value={crearNombre}
            onChange={(e) => setCrearNombre(e.target.value)}
            placeholder="ej. Distribuciones y Sistemas"
          />
        </form>
      </Modal>
    </div>
  );
}
